/**
 * Campaign Management Types
 *
 * Types for managing campaigns via REST API and Redis storage.
 * Campaigns are configured dynamically via admin dashboard instead of hardcoded.
 *
 * Three main data structures:
 * 1. HostCampaignConfig - Launch configuration for hosts
 * 2. PlayerCampaignTheme - UI theme for player marketing pages
 * 3. CampaignUrlMapping - URL slug → campaign ID mappings
 *
 * NOTE: PlayerCampaignTheme is a minimal type. The full QuickstartTheme type
 * in marketing-demo/src/features/quickstart/types/theme.ts is the authoritative source.
 * This type only defines the core fields for API validation.
 */

import { z } from 'zod';
import { launchPackageSchema, cooldownStartTriggerSchema } from './campaign';
import { macroTreeGraphSchema } from './macroTree';

// ============================================================================
// HOST CAMPAIGN CONFIG (for game launching)
// ============================================================================
// Note: Reuses launchPackageSchema from campaign.ts to avoid duplication

/**
 * Machine restrictions for campaigns
 * Controls which machines are allowed/blocked from running a campaign
 */
export const machineRestrictionsSchema = z.object({
  mode: z.enum(['all', 'allowlist', 'blocklist']).default('all'),
  machines: z.array(z.string()).default([]), // Array of machineIds
});

export type MachineRestrictions = z.infer<typeof machineRestrictionsSchema>;

/**
 * The input methods a campaign can declare support for.
 * Used to clamp the input access granted to players at join time.
 */
export const playerInputTypeSchema = z.enum(['gamepad', 'keyboard', 'mouse', 'touch']);

export type PlayerInputType = z.infer<typeof playerInputTypeSchema>;

/**
 * Host campaign configuration (stored in Redis)
 */
export const hostCampaignConfigSchema = z.object({
  campaignId: z.string(),
  displayName: z.string(),
  active: z.boolean(),
  edition: z.literal('pavilion'),
  launchPackage: launchPackageSchema,
  onLaunchMacro: z.array(z.string()),
  onJoinMacro: z.array(z.string()),
  onExitMacro: z.array(z.string()).optional().default([]),
  onInactiveMacro: z.array(z.string()).optional().default([]),
  machineRestrictions: machineRestrictionsSchema.optional(), // Optional for backwards compatibility
  isPublic: z.boolean().default(false), // Whether campaign appears in public carousel/enumeration
  publicSlug: z.string().optional(), // URL slug used by the public carousel when isPublic is true
  defaultSlug: z.string().optional(), // URL slug used by the game gallery and as the default demo link when active is true
  restartOnDisconnect: z.boolean().default(true), // Whether to restart game when all players disconnect (Ad Mode only)
  deploymentScriptId: z.string().optional(), // ID of the approved provisioning script used for campaign deployment
  // Per-execution script timeout. Overrides the script-level default when set.
  // Min 30 s (prevents trivially-short deployments), max 14 400 s (4 h).
  deploymentTimeoutSeconds: z.number().int().min(30).max(14400).optional(),
  // How many hours after creation the entire deployment auto-expires.
  // Converted to an absolute expiresAt timestamp at wizard submit time.
  // Min 1 h, max 168 h (1 week). `.positive()` alone allows fractional hours
  // below 1 (e.g. 0.5), which contradicts the doc'd minimum and the wizard's
  // hour-granular UI; `.min(1)` plus the existing `.max(168)` enforces the
  // canonical bounded range.
  deploymentExpiryHours: z.number().min(1).max(168).optional(),
  // Matchmaking cooldown configuration (host-side authority).
  // See libs/Messaging/src/types/api/campaign.ts for semantics. Hitman uses
  // cooldownStartTrigger: 'onFirstPlayerJoined' with cooldownPeriodMs: 1_800_000.
  // Both fields are optional; the host defaults trigger to `'never'` and period to `0`.
  cooldownStartTrigger: cooldownStartTriggerSchema.optional(),
  cooldownPeriodMs: z.number().int().min(0).optional(),
  // Opt-in flag for the stateful macro system. When true, the host's
  // GameLaunchCoordinator skips its legacy onLaunchMacro path and the
  // StatefulMacroCoordinator drives launch readiness via MACRO_WALK_*
  // messages. Default-undefined preserves existing-campaign behavior.
  useStatefulMacroSystem: z.boolean().optional(),

  // Scheduled screenshot watcher: periodically captures a screenshot while
  // idle and compares its perceptual hash against the reference. Triggers a
  // game restart on mismatch (stuck/crashed game self-heal).
  scheduledWatcher: z.object({
    enabled: z.boolean(),
    referenceImageHash: z.string(),
    previewUrl: z.string().optional(),
    captureWidth: z.number().int().positive().default(1920),
    captureHeight: z.number().int().positive().default(1080),
    intervalMs: z.number().int().min(30000).default(300000),
    hashTolerance: z.number().int().min(0).max(64).default(20),
  }).optional(),

  // Input methods this campaign supports. When set, the host clamps each
  // player's input access (gamepad/keyboard/mouse/touch) to this list at join
  // time. Undefined or empty = unrestricted (legacy behavior, all inputs as
  // governed by the host's global input state). A campaign that includes
  // 'touch' grants touch access without requiring the host's debug touch toggle.
  supportedInputs: z.array(playerInputTypeSchema).optional(),

  // Game-instance model (docs/architecture/matchmaking-v2.md). Defaults
  // preserve single-game v1 behavior for existing campaigns:
  // one instance per host, one player per instance, v1 matchmaker.
  gameInstancesPerHost: z.number().int().min(1).max(64).default(1),
  maxPlayersPerInstance: z.number().int().min(1).max(16).default(1),
  matchmakingVersion: z.enum(['v1', 'v2']).default('v1'),

});

export type HostCampaignConfig = z.infer<typeof hostCampaignConfigSchema>;

// ============================================================================
// PLAYER CAMPAIGN THEME (for marketing pages)
// ============================================================================

/**
 * Gamepad button chain configuration for mobile
 */
export const gamepadButtonChainSchema = z.object({
  trigger: z.string(), // GamepadButton enum value
  prerequisite: z.string().optional(),
  maintainWhileHeld: z.boolean().optional(),
  chainDelayMs: z.number().int().min(0).max(2000).optional(),
});

/**
 * Mobile gamepad configuration
 * Uses passthrough to allow dynamic properties like customButtonIcons and customButtonSizes
 */
export const mobileGamepadConfigSchema = z.object({
  buttonChains: z.array(gamepadButtonChainSchema).optional(),
  defaultLayout: z.any().optional(),
  defaultHiddenButtons: z.array(z.string()).optional(),
  invisibleButtons: z.array(z.string()).optional(),
  defaultOpacity: z.number().int().min(0).max(100).optional(),
  useCircularButtons: z.boolean().optional(),
  customButtonIcons: z.any().optional(),
  customButtonSizes: z.any().optional(),
  rightThumbstickBehindButtons: z.boolean().optional(),
  attractModeConfig: z.object({
    buttonPattern: z.array(z.enum(['A', 'B', 'X', 'Y'])),
    highlightDurationMs: z.number().int().min(100).max(5000),
    highlightOpacity: z.number().int().min(0).max(100),
    backgroundOpacity: z.number().int().min(0).max(100).optional(),
  }).optional(),
  hintButton: z.enum(['A', 'B', 'X', 'Y', 'LB', 'RB', 'LT', 'RT', 'DpadUp', 'DpadDown', 'DpadLeft', 'DpadRight', 'Start', 'Select']).optional(),
}).passthrough();

/**
 * Player campaign theme (stored in Redis)
 * Used by marketing-demo for theming the player experience
 *
 * NOTE: This schema is kept minimal for backwards compatibility.
 * The full theme type is defined in marketing-demo/src/features/quickstart/types/theme.ts
 * Schema validation is intentionally loose to allow for schema evolution.
 */
export const playerCampaignThemeSchema = z.object({
  slug: z.string(), // Campaign identifier (same as campaignId)
  gameName: z.string(),
  studioName: z.string(),

  // Media URLs (S3 paths)
  studioLogo: z.string().url().optional(),
  thankYouStudioLogo: z.string().url().optional(),
  gameLogo: z.string().url().optional(),
  pageBackgroundImage: z.string().url().optional(),
  playNowModalBackgroundImage: z.string().url().optional(),

  // Colors (hex format)
  primaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
  secondaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
  modalBackgroundColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
  modalBackgroundOpacity: z.number().min(0).max(1),
  playNowModalBackgroundColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
  playNowModalBackgroundOpacity: z.number().min(0).max(1).optional(),
  releaseDateTextColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
  badgeOverrideTextColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
  spinnerColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),

  // Release information
  releaseDate: z.string().optional(),
  releaseDateTimestamp: z.number().int().optional(),

  // External links
  steamUrl: z.string().url().optional(),
  discordUrl: z.string().url().optional(),
  storePlatform: z.string(),

  // Timing configuration
  afkTimeoutSeconds: z.number().int().positive(),
  adTimerSeconds: z.number().int().positive(),
  inGameIdleTimeoutSeconds: z.number().int().nonnegative().optional(),

  // Behavior configuration
  afkTimerBehavior: z.enum(['reset', 'cumulative']),
  preferredAuthProvider: z.enum(['google', 'discord', 'apple']).optional(),
  fullscreenExitBehavior: z.enum(['redirect_to_cta', 'return_to_afk_with_overlay']),
  requireAuth: z.boolean().optional(), // Whether to require Clerk authentication after demo ends

  // Email configuration
  sendWishlistEmail: z.boolean().optional(),
  showMobileHomeScreenPrompt: z.boolean().optional(),

  // Home screen / PWA configuration (for per-campaign subdomain icons)
  appIconUrl: z.string().url().optional(),
  appShortName: z.string().max(12).optional(),
  homeScreenSubdomain: z.string().regex(/^[a-z0-9-]+$/).optional(),
  ogImageUrl: z.string().url().optional(),
  ogTitle: z.string().max(70).optional(),
  ogDescription: z.string().max(200).optional(),

  // Wishlist email template fields (only used in emails, not on demo site)
  wishlistEmailGameTitleUrl: z.string().url().optional(),
  wishlistEmailGameImageUrl: z.string().url().optional(),
  wishlistEmailGamePlaycastLogoUrl: z.string().url().optional(),
  wishlistEmailGameNameText: z.string().optional(),
  wishlistEmailSteamUrl: z.string().url().optional(),

  // In-game UI configuration
  timerPosition: z.enum(['top', 'bottom']).optional(),
  inGameCtaText: z.string().optional(),

  // Mobile configuration
  mobileGamepadConfig: mobileGamepadConfigSchema.optional(),

  // Discord Activity configuration (optional)
  discordActivity: z.object({
    // Discord OAuth credentials
    appId: z.string(), // Discord Application ID (same as Client ID)
    // Note: clientSecret stored separately in Redis, never exposed to clients

    // Discord Rich Presence settings
    discordRichPresence: z.object({
      description: z.string(),
      state: z.string(),
      largeImage: z.string(),
      largeText: z.string(),
      buttonLabel: z.string(),
      buttonUrl: z.string().url(),
    }),

    // Discord-specific media (optional, supplements existing campaign media)
    discordMinimizedImage: z.string().url().optional(),
    discordThanksImage2: z.string().url().optional(),
    discordJoinButtonFrame: z.string().url().optional(),
    discordFullscreenButtonFrame: z.string().url().optional(),
    discordPoweredByImage: z.string().url().optional(),
  }).optional(),
}).passthrough(); // Allow additional properties not defined in schema

/**
 * Minimal PlayerCampaignTheme type for backwards compatibility
 * Uses Record<string, any> for dynamic properties that may be added over time
 * This allows the type to evolve without breaking existing code
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type PlayerCampaignTheme = z.infer<typeof playerCampaignThemeSchema> & Record<string, any>;
export type MobileGamepadConfig = z.infer<typeof mobileGamepadConfigSchema>;
export type GamepadButtonChain = z.infer<typeof gamepadButtonChainSchema>;

/**
 * Discord Activity settings (optional extension to PlayerCampaignTheme)
 */
export type DiscordActivitySettings = {
  appId: string; // Discord Application ID (same as Client ID)
  discordRichPresence: {
    description: string;
    state: string;
    largeImage: string;
    largeText: string;
    buttonLabel: string;
    buttonUrl: string;
  };
  discordMinimizedImage?: string;
  discordThanksImage2?: string;
  discordJoinButtonFrame?: string;
  discordFullscreenButtonFrame?: string;
  discordPoweredByImage?: string;
};

// ============================================================================
// URL MAPPINGS
// ============================================================================

/**
 * UTM parameters for URL tracking
 * Optional parameters that can be attached to URL mappings for campaign tracking
 */
const utmParamsBaseSchema = z.object({
  utm_source: z.string().optional(),
  utm_campaign: z.string().optional(),
  utm_content: z.string().optional(),
  utm_term: z.string().optional(),
  utm_id: z.string().optional(),
  utm_medium: z.string().optional(),
});

export const utmParamsSchema = utmParamsBaseSchema.optional();

export type UtmParams = z.infer<typeof utmParamsBaseSchema>;

/**
 * Campaign URL mapping (stored in Redis)
 * Maps URL slug to campaign ID (many-to-one)
 * Optionally includes UTM parameters for tracking
 */
export const campaignUrlMappingSchema = z.object({
  urlSlug: z.string().regex(/^[a-zA-Z0-9_-]+$/), // URL-safe characters only
  campaignId: z.string(),
  createdAt: z.number().int(),
  utmParams: utmParamsSchema,
});

export type CampaignUrlMapping = z.infer<typeof campaignUrlMappingSchema>;

// ============================================================================
// SLUG URL CONFIG (per-slug CTA/tracker + dynamic params + pass-through)
// ============================================================================

/**
 * Dynamic inbound parameter definition.
 * Each entry is a URL param key the system will look for on the landing URL.
 * If not found, the optional staticFallback value is used instead.
 */
export const dynamicInboundParamSchema = z.object({
  key: z.string().min(1),
  staticFallback: z.string().optional(),
});

export type DynamicInboundParam = z.infer<typeof dynamicInboundParamSchema>;

/**
 * Per-parameter pass-through toggles.
 * Controls which outbound URLs receive this captured parameter.
 */
export const paramPassThroughSchema = z.object({
  paramKey: z.string().min(1),
  iosCta: z.boolean(),
  iosTracker: z.boolean(),
  androidCta: z.boolean(),
  androidTracker: z.boolean(),
});

export type ParamPassThrough = z.infer<typeof paramPassThroughSchema>;

/**
 * Group-level pass-through toggle for all custom UTMs.
 * Custom UTMs are all-or-nothing per outbound URL.
 */
export const customUtmPassThroughSchema = z.object({
  iosCta: z.boolean(),
  iosTracker: z.boolean(),
  androidCta: z.boolean(),
  androidTracker: z.boolean(),
});

export type CustomUtmPassThrough = z.infer<typeof customUtmPassThroughSchema>;

/**
 * A saved link variant with static UTMs for copy/paste.
 * Represents a specific campaign context (e.g. "Facebook - Summer 2025").
 */
export const savedLinkVariantSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  params: z.record(z.string(), z.string()),
  createdAt: z.number().int(),
});

export type SavedLinkVariant = z.infer<typeof savedLinkVariantSchema>;

/**
 * Per-device URL pair (CTA + optional tracker).
 */
export const deviceUrlPairSchema = z.object({
  ctaUrl: z.string(),
  trackerUrl: z.string().optional(),
});

export type DeviceUrlPair = z.infer<typeof deviceUrlPairSchema>;

/**
 * Complete per-slug URL configuration.
 * Locked to a specific slug — defines destination URLs, dynamic params,
 * pass-through rules, and saved link variants for that slug's channel.
 */
export const slugUrlConfigSchema = z.object({
  ios: deviceUrlPairSchema,
  android: deviceUrlPairSchema,
  dynamicInboundParams: z.array(dynamicInboundParamSchema),
  passThrough: z.object({
    params: z.array(paramPassThroughSchema),
    customUtms: customUtmPassThroughSchema,
  }),
  savedLinks: z.array(savedLinkVariantSchema),
});

export type SlugUrlConfig = z.infer<typeof slugUrlConfigSchema>;

// ============================================================================
// FLOW GRAPH (re-exported from dedicated file)
// ============================================================================

export type { QuickstartPhase, FlowNode, FlowEdge, FlowGraph, FlowGraphMetadata } from './flowGraph';
export { PHASE_COMPONENT_MAP, VALID_PHASES, DEFAULT_FLOW_GRAPH, PHASE_DISPLAY_NAMES } from './flowGraph';

// ============================================================================
// COMBINED CAMPAIGN (for admin dashboard editing)
// ============================================================================

/**
 * Complete campaign configuration (all three parts combined)
 * Used for admin dashboard import/export
 */
export const completeCampaignConfigSchema = z.object({
  hostConfig: hostCampaignConfigSchema,
  playerTheme: playerCampaignThemeSchema,
  urlMappings: z.array(campaignUrlMappingSchema),
  flowGraph: z.object({
    nodes: z.array(z.any()),
    edges: z.array(z.any()),
    metadata: z.object({
      version: z.string(),
      createdAt: z.number(),
      updatedAt: z.number(),
      createdBy: z.string().optional(),
    }),
  }).optional(),
  /**
   * Per-slug URL configurations (destination URLs, pass-through rules, link
   * variants). Keyed by slug string. Optional — campaigns without slug URL
   * configs simply omit this field. savedLinks are included so exported
   * link variants survive the round-trip.
   */
  slugUrlConfigs: z.record(z.string(), slugUrlConfigSchema).optional(),
  /**
   * Stateful-macro trees, keyed by trigger. Optional — campaigns that have
   * not opted into the stateful macro system simply omit this field. v1
   * supports only the `'launch'` trigger; future v2 triggers (`join`,
   * `exit`, `inactive`) plug in as additional optional keys without a
   * schema bump. Snapshot URLs travel verbatim via copy-by-reference; the
   * realtime-api's macroTreeService applies refcounted-delete semantics
   * so cross-campaign references can't accidentally orphan each other.
   */
  macroTrees: z.object({
    launch: macroTreeGraphSchema.optional(),
  }).optional(),
});

export type CompleteCampaignConfig = z.infer<typeof completeCampaignConfigSchema>;

// ============================================================================
// API REQUEST/RESPONSE TYPES
// ============================================================================

/**
 * API response wrapper
 */
export const apiResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    success: z.boolean(),
    data: dataSchema.optional(),
    error: z.string().optional(),
  });

/**
 * URL resolution response
 */
export const urlResolutionResponseSchema = z.object({
  campaignId: z.string(),
  exists: z.boolean(),
});

export type UrlResolutionResponse = z.infer<typeof urlResolutionResponseSchema>;

/**
 * Campaign list item (for admin dashboard)
 */
export const campaignListItemSchema = z.object({
  campaignId: z.string(),
  displayName: z.string(),
  active: z.boolean(),
  isPublic: z.boolean().optional(), // Optional for backwards compatibility
  urlSlugs: z.array(z.string()),
  lastModified: z.number().int().optional(),
});

export type CampaignListItem = z.infer<typeof campaignListItemSchema>;

/**
 * Media upload request
 */
export const mediaUploadRequestSchema = z.object({
  slot: z.enum(['studioLogo', 'thankYouStudioLogo', 'gameLogo', 'pageBackgroundImage', 'playNowModalBackgroundImage']),
  fileName: z.string(),
  fileSize: z.number().int().positive(),
  contentType: z.string(),
});

export type MediaUploadRequest = z.infer<typeof mediaUploadRequestSchema>;

/**
 * Media upload response (presigned URL)
 */
export const mediaUploadResponseSchema = z.object({
  uploadUrl: z.string().url(),
  publicUrl: z.string().url(),
  expiresIn: z.number().int(),
});

export type MediaUploadResponse = z.infer<typeof mediaUploadResponseSchema>;

